import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TourAvailabilityResponse, BookingResponse } from '../interfaces/tour-booking.interface';
import { LeadCaptureWidgetSubmission } from '../interfaces/lead-capture-submission.interface';
import { WidgetRecaptchaSubmitPayload } from './recaptcha/widget-recaptcha.types';
import { FormSubmissionService } from './form-submission.service';
import { CONST_URL_SEGEMENTS } from 'paylink-constants';
import { WIDGETS_B2B_API_URL } from '../tokens/widgets-b2b-api-url.token';
import { buildWidgetApiPath } from '../utils/widget-api-path.util';

@Injectable({
  providedIn: 'root',
})
export class TourBookingService {
  private readonly http = inject(HttpClient);
  private readonly formSubmissionService = inject(FormSubmissionService);
  private readonly b2bApiUrl = inject(WIDGETS_B2B_API_URL);

  getTourAvailability(
    companyUuid: string,
    tenantUuid: string,
    startDate: string,
    endDate: string,
  ): Observable<TourAvailabilityResponse> {
    const params = new HttpParams()
      .set('start_date', startDate)
      .set('end_date', endDate);

    const path = buildWidgetApiPath(
      CONST_URL_SEGEMENTS.OPEN_API.WIDGETS.GET_TOUR_AVAILABILITY,
      { company_uuid: companyUuid, tenant_uuid: tenantUuid },
    );
    const url = `${this.b2bApiUrl}${path}`;

    return this.http.get<TourAvailabilityResponse>(url, { params }).pipe(
      map((response) => {
        response.Staffs?.forEach((staff) => {
          staff.days?.forEach((day) => {
            day.spots = day.spots ?? [];
            day.spots.sort();
          });
        });
        return response;
      }),
    );
  }

  getConsolidatedSlots(
    response: TourAvailabilityResponse,
    date: string,
  ): string[] {
    const allSlots = new Set<string>();

    response.Staffs?.forEach((staff) => {
      const dayData = staff.days?.find((day) => day.date.startsWith(date));
      if (dayData?.spots) {
        dayData.spots.forEach((spot) => allSlots.add(spot));
      }
    });

    return Array.from(allSlots).sort();
  }

  submitTourBooking(
    widgetId: string,
    payload: LeadCaptureWidgetSubmission,
    captcha?: WidgetRecaptchaSubmitPayload,
  ): Observable<BookingResponse> {
    if (!widgetId) {
      throw new Error('Widget ID is required for tour booking');
    }

    return this.formSubmissionService.submitTourBookingLeadCapture(
      widgetId,
      payload,
      captcha,
    );
  }
}
